import { Input } from '@chakra-ui/react';
import { Editor } from '@tiptap/core';
import { RootState, store, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { updateWidget } from 'modules/Editor/store/infographSlice';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { WidgetId } from 'types/idTypes';
import { replaceTextContent } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.helpers';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { getTextWidgetProperties } from 'widgets/TextBasedWidgets/TextWidget/TextWidgetData.helpers';
import { getReadOnlyExtensions } from 'widgets/TextBasedWidgets/TextWidget/TextWidgetExtensions';
import { TextWidgetTag } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';

type MultipleChoicesSettingsTextInputProps = {
  widgetId: WidgetId;
};

export const MultipleChoicesSettingsTextInput = ({ widgetId }: MultipleChoicesSettingsTextInputProps) => {
  const dispatch = useAppDispatch();
  const questionText =
    getTextWidgetProperties((useAppSelector(selectWidget(widgetId)) as TextWidgetData)?.proseMirrorData)?.text || '';

  const [inputValue, setInputValue] = useState<string>(questionText);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleUpdate = useCallback(
    (text: string) => {
      const storeState = store.getState() as RootState;
      const widget = selectWidget(widgetId)(storeState) as TextWidgetData;
      const extensions = getReadOnlyExtensions(TextWidgetTag.Paragraph, false, false);
      const editor = new Editor({ extensions, content: widget.proseMirrorData, editable: false });

      replaceTextContent(editor, text);

      const parsedProseMirrorData = editor.getJSON();
      dispatch(
        updateWidget({
          widgetId,
          widgetData: { proseMirrorData: parsedProseMirrorData },
        }),
      );
    },
    [widgetId, dispatch],
  );

  // Update when the text widget changes from the store
  useEffect(() => setInputValue(questionText), [questionText]);

  return (
    <Input
      size='sm'
      borderRadius={'base'}
      value={inputValue}
      onChange={handleChange}
      onBlur={(e: ChangeEvent<HTMLInputElement>) => handleUpdate(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleUpdate(e.currentTarget.value);
      }}
    />
  );
};
